import { Component, FormEventHandler, ChangeEventHandler } from 'react'
import * as styles from 'styles/molecules/CreditLinkageQuestions.css'
import * as cn from 'classnames'
import Button from 'components/features/pfm/Button/Button'
import * as range from 'lodash/range'
import { CreditQuestion as CreditQuestionType } from 'interfaces/credit'

import { connect } from 'react-redux'
import { State } from 'interfaces/state'
import { sendCreditAnswersActionCreator, setCreditAnswerActionCreator } from 'actions/creditActions'
import OldRadioButton from 'components/atoms/inputs/OldRadioButton'

export type CreditLinkageStateProps = {
  questions: CreditQuestionType[]
  questionIndex: number
}

export type CreditLinkageDispatchProps = {
  sendCreditAnswers: typeof sendCreditAnswersActionCreator
  setCreditAnswer: typeof setCreditAnswerActionCreator
}

type CreditLinkageProps = CreditLinkageStateProps & CreditLinkageDispatchProps

type CreditQuestionState = CreditLinkageStateProps & {
  selectedAnswerId: number | null
}

class CreditLinkageQuestionsComponent extends Component<CreditLinkageProps, CreditQuestionState> {
  static getDerivedStateFromProps (nextProps: CreditLinkageProps, prevState: CreditQuestionState) {
    const isCurrentQuestionChanged = nextProps.questions !== prevState.questions
      || nextProps.questionIndex !== prevState.questionIndex

    if (isCurrentQuestionChanged) {
      const selectedAnswerId = nextProps.questions[nextProps.questionIndex].answer_choices[0].id
      return {
        selectedAnswerId,
        questions: nextProps.questions,
        questionIndex: nextProps.questionIndex,
      }
    }

    return null
  }

  constructor (props: CreditLinkageProps, context: any) {
    super(props, context)

    this.state = {
      selectedAnswerId: props.questions[props.questionIndex].answer_choices[0].id,
      questions: this.props.questions,
      questionIndex: this.props.questionIndex,
    }
  }

  render () {
    const {
      questions,
      questionIndex,
    } = this.props
    const question = questions[questionIndex]

    if (!question) {
      return null
    }

    return (
      <section className={ styles.creditLinkageQuestions }>
        { this.renderDots() }
        { this.renderQuestion(question) }
      </section>
    )
  }

  renderDots () {
    const { questions, questionIndex } = this.props

    return (
      <div className={ styles.dotsPager } data-qa-marker='dots-pager'>
        {
          range(questions.length).map((i) => {
            const className = cn(styles.dot, {
              [styles.dotActive]: i === questionIndex,
            })

            return (
              <span key={ i } className={ className } />
            )
          })
        }
      </div>
    )
  }

  renderQuestion (question: CreditQuestionType) {
    const { id, text, answer_choices } = question
    const { selectedAnswerId } = this.state

    return (
      <form className={ styles.creditQuestion } onSubmit={ this.handleSubmit }>
        <header className={ styles.questionTitle } data-qa-marker='question-title'>
          { text }
        </header>
        <section className={ styles.answers }>
          {
            answer_choices.map((answer) => (
              <div
                key={ answer.id }
                className={ styles.answer }
              >
                <OldRadioButton
                  label={ answer.text }
                  name={ `question-${ id }` }
                  value={ String(answer.id) }
                  checked={ selectedAnswerId === answer.id }
                  onChange={ this.handleOptionChange }
                  autoFocus={ selectedAnswerId === answer.id }
                  qaMarker={ `answer-${ id }` }
                />
              </div>
            ))
          }
        </section>
        <Button
          qaMarker='submit-button'
          size='small'
          theme='gray'
          fullWidth
          submit
        >
          Submit Answer
        </Button>
      </form>
    )
  }

  private handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()

    const {
      questions,
      questionIndex,
      setCreditAnswer,
      sendCreditAnswers,
    } = this.props
    const { selectedAnswerId } = this.state

    if (selectedAnswerId === null) {
      return
    }

    setCreditAnswer({
      choice: selectedAnswerId,
      question: questions[questionIndex].id,
    })

    if (questionIndex >= questions.length - 1) {
      // we will go to next step inside epic
      // after successfully sending credit answer
      sendCreditAnswers()
    }
  }

  private handleOptionChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const nextValue = Number(e.target.value)
    this.setState({
      selectedAnswerId: nextValue,
    })
  }
}

const mapStateToProps = (state: State): CreditLinkageStateProps => ({
  questions: state.app.creditscore.linkage.questions,
  questionIndex: state.app.creditscore.linkage.questionIndex,
})

const mapDispatchToProps: CreditLinkageDispatchProps = {
  sendCreditAnswers: sendCreditAnswersActionCreator,
  setCreditAnswer: setCreditAnswerActionCreator,
}

const CreditLinkageQuestions = connect<CreditLinkageStateProps, CreditLinkageDispatchProps>(
  mapStateToProps, mapDispatchToProps
)(CreditLinkageQuestionsComponent)
export default CreditLinkageQuestions
